import { Network } from '@movilizame/networking';
import {_parseGoogleResult, _parseOSMResult} from './../services/utility';

export class Geocoding {
    constructor (runtime) {
        this._runtime = runtime || 'google';
    }

    async _getRoutesLibrary () {
        /* eslint-disable no-undef */
        if (typeof google === 'undefined' || !google.maps) {
            throw new Error('Google Maps not available');
        }

        // Prefer modern dynamic import when available.
        if (typeof google.maps.importLibrary === 'function') {
            // @ts-ignore
            return google.maps.importLibrary('routes');
        }

        // Fallback: assume routes library was loaded via script tag (libraries=routes).
        return { Route: google.maps.routes && google.maps.routes.Route ? google.maps.routes.Route : null };
    }

    _formatDistance (meters) {
        if (typeof meters !== 'number' || Number.isNaN(meters)) return null;
        if (meters >= 1000) return (meters / 1000).toFixed(2) + ' km';
        return Math.round(meters) + ' m';
    }

    _formatDuration (millis) {
        if (typeof millis !== 'number' || Number.isNaN(millis)) return null;
        const minutes = Math.round(millis / 60000);
        if (minutes < 60) return minutes + ' min';
        const hours = Math.floor(minutes / 60);
        const rest = minutes % 60;
        return rest ? `${hours} h ${rest} min` : `${hours} h`;
    }

    reverse (location) {
        return new Promise((resolve, reject) => {
            let reverseGeo = null;
            /* eslint-disable no-undef */
            if (this._runtime === 'google') {
                let networking = new google.maps.Geocoder();
                let data = {
                    location: location
                };
                networking.geocode(data, function (results, status) {
                    if (status === google.maps.GeocoderStatus.OK) {
                        if (results[0]) {
                            reverseGeo = _parseGoogleResult(results[0]);
                            resolve(reverseGeo);
                        } else {
                            // No results
                            reject();
                        }
                    } else {
                        if (status === google.maps.GeocoderStatus.OVER_QUERY_LIMIT) {
                            console.log('Geocode.OVER_QUERY_LIMIT');
                        }
                        reject();
                    }
                });
            } else if (this._runtime === 'photon') {
                // Photon geocoding service (demo server)
                let networking = new Network('https://photon.komoot.io');
                let url = '/reverse?lat=' + location.lat + '&lon=' + location.lng;
                networking.get(url, {}).then((photon) => {
                    if (photon.features && photon.features.length > 0) {
                        let feature = photon.features[0];
                        let properties = feature.properties || {};
                        // Convert Photon format to OSM-like format for parsing
                        let osmAddress = {
                            road: properties.street || properties.name || null,
                            house_number: properties.housenumber || null,
                            suburb: properties.district || properties.suburb || properties.neighbourhood || null,
                            country: properties.country || null,
                            state: properties.state || properties.region || null,
                            city: properties.city || properties.town || properties.name || null
                        };
                        reverseGeo = _parseOSMResult(osmAddress);
                        resolve(reverseGeo);
                    } else {
                        reject();
                    }
                }).catch((error) => {
                    console.error('Photon geocoding error:', error);
                    reject();
                });
            } else {
                // Default to OSM/Nominatim
                let networking = new Network('https://nominatim.openstreetmap.org');
                let url = '/reverse?format=json&';
                url += '&lat=' + location.lat + '&lon=' + location.lng + '&zoom=18&addressdetails=1';
                networking.get(url, {}).then((osm) => {
                    if (osm.address) {
                        reverseGeo = _parseOSMResult(osm.address);
                        resolve(reverseGeo);
                    } else {
                        reject();
                    }
                }).catch(() => {
                    reject();
                });
            }
        }, () => {

        });
    }

    calculateRoute (origin, destination) {
        return new Promise((resolve, reject) => {
            let route = {
                distance: 10,
                distanceValue: 0,
                time: 25,
                timeValue: 0,
                polyline: [],
                route: {}
            };
            if (this._runtime === 'google') {
                (async () => {
                    try {
                        /* eslint-disable no-undef */
                        const lib = await this._getRoutesLibrary();
                        const Route = lib && lib.Route ? lib.Route : (google.maps.routes && google.maps.routes.Route ? google.maps.routes.Route : null);

                        // If routes library isn't available, fall back to legacy DirectionsService.
                        if (!Route || typeof Route.computeRoutes !== 'function') {
                            let directionsService = new google.maps.DirectionsService();
                            const request = { origin, destination, travelMode: 'DRIVING' };
                            directionsService.route(request, function (result, status) {
                                if (status === 'OK') {
                                    if (result.routes && result.routes.length && result.routes[0].legs && result.routes[0].legs.length) {
                                        route.distance = result.routes[0].legs[0].distance.text;
                                        route.distanceValue = result.routes[0].legs[0].distance.value;
                                        route.time = result.routes[0].legs[0].duration.text;
                                        route.timeValue = result.routes[0].legs[0].duration.value;
                                        route.route = result.routes[0].legs[0];
                                        resolve(route);
                                    } else {
                                        reject();
                                    }
                                } else {
                                    reject();
                                }
                            });
                            return;
                        }

                        const request = {
                            origin,
                            destination,
                            travelMode: 'DRIVING',
                            fields: ['distanceMeters', 'durationMillis', 'path', 'legs']
                        };

                        const { routes } = await Route.computeRoutes(request);
                        if (!routes || !routes.length) {
                            reject();
                            return;
                        }

                        const r = routes[0];
                        route.distanceValue = r.distanceMeters || 0;
                        route.timeValue = r.durationMillis ? Math.round(r.durationMillis / 1000) : 0;
                        route.distance = this._formatDistance(route.distanceValue) || route.distance;
                        route.time = this._formatDuration(r.durationMillis) || route.time;
                        route.polyline = r.path || [];
                        route.route = (r.legs && r.legs[0]) ? r.legs[0] : r;

                        resolve(route);
                    } catch (e) {
                        reject(e);
                    }
                })();
            } else {
                // Use OSRM for non-google runtimes
                let networking = new Network('https://router.project-osrm.org');
                // Format: /route/v1/driving/{coordinates}?overview=false&alternatives=true&steps=true
                let coordinates = `${origin.lng},${origin.lat};${destination.lng},${destination.lat}`;
                let url = `/route/v1/driving/${coordinates}?overview=false&alternatives=false&steps=false`;

                networking.get(url, {}).then((osrm) => {
                    if (osrm.code === 'Ok' && osrm.routes && osrm.routes.length > 0) {
                        let osrmRoute = osrm.routes[0];
                        // Convert distance from meters to km
                        let distanceKm = (osrmRoute.distance / 1000).toFixed(2);
                        route.distance = distanceKm + ' km';
                        route.distanceValue = osrmRoute.distance;

                        // Convert duration from seconds to minutes
                        let durationMinutes = Math.round(osrmRoute.duration / 60);
                        route.time = durationMinutes + ' min';
                        route.timeValue = osrmRoute.duration;

                        // Store the route geometry for polyline if needed
                        if (osrmRoute.geometry) {
                            route.polyline = osrmRoute.geometry;
                        }

                        route.route = osrmRoute;
                        resolve(route);
                    } else {
                        reject();
                    }
                }).catch(() => {
                    reject();
                });
            }
        }, () => {
        });
    }

}



// WEBPACK FOOTER //
// ./src/services/geocoding.js